import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import Container from 'reactstrap/lib/Container';

import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { EDITORIAL_HEADER_SRC_INLINE } from 'site-modules/shared/constants/editorial/editorial-header';
import { RESEARCH_LINK_WIDGET_EDITORIAL_PATH } from 'site-modules/shared/constants/editorial/research-article';

import { EditorialMap } from 'client/tracking/maps/editorial';
import { TrackingHandler } from 'client/tracking/handler';

import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities, CmsModel, CmsPaths } from 'client/data/models/cms';

import { getObdDtcBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { getCanonical } from 'site-modules/shared/utils/seo-helper';
import { renderEntries } from 'site-modules/editorial/utils/entries-renderer';
import { getResearchArticleContentPath } from 'site-modules/editorial/utils/content-path';
import { transformAllAuthorsContent } from 'site-modules/shared/utils/editorial/author';

import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { ArticleMetadata } from 'site-modules/shared/components/editorial/article-metadata/article-metadata';
import { AllAuthorsMetadata } from 'site-modules/shared/components/editorial/author-metadata/all-authors-metadata';
import { Share } from 'site-modules/shared/components/share/share';
import { VehicleShowcase } from 'site-modules/shared/components/upper-funnel/vehicle-showcase/vehicle-showcase';
import { AppraiserSellModule } from 'site-modules/shared/components/appraiser-sell/appraiser-sell';
import { CarNewsMarquee } from 'site-modules/shared/components/upper-funnel/home-marquee/car-news-marquee';

import { ARTICLE_BREADCRUMBS, ARTICLE_CATEGORY } from './constants';

function ObdDtcCodeUI({
  content = null,
  allAuthors = {
    author: DEFAULT_CONTENT,
    coAuthor: DEFAULT_CONTENT,
    editor: DEFAULT_CONTENT,
  },
  linkWidgetContent = DEFAULT_CONTENT,
  isMobile = false,
}) {
  useEffect(() => {
    TrackingHandler.useMap(EditorialMap);
  }, []);

  if (!content?.title) {
    return null;
  }

  const { title } = content;
  const canonicalURL = getCanonical(content);
  const contentChildren = content.children();

  return (
    <main className="research-article article-no-hero mb-3_5 pos-r">
      <div className="pos-r mb-1">
        <img
          src={EDITORIAL_HEADER_SRC_INLINE[isMobile]}
          alt=""
          fetchPriority="high"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: '0',
            width: '100%',
            height: '100%',
          }}
        />
        <Container className="pb-1">
          <Breadcrumbs className="ps-0 text-white mb-1_5" data={getObdDtcBreadcrumbs(title, ARTICLE_BREADCRUMBS)} />
          <div className="heading-container mb-3">
            <h1
              className={classnames('text-white fw-bold text-center pb-md-2 mb-0', {
                'size-36': !isMobile,
                'size-24': isMobile,
              })}
            >
              {title}
            </h1>
          </div>
        </Container>
      </div>
      <Container>
        <ArticleMetadata
          className="justify-content-center mb-2"
          content={content}
          allAuthors={allAuthors}
          creativeId="edm-entry-article-metadata"
          isMobile={isMobile}
        />
        <div className="size-16 mb-0_5">{renderEntries({ entriesToRender: contentChildren })}</div>
        <CarNewsMarquee className="mt-3" scrollBtnPosition={isMobile ? 'start' : 'end'} />
        <hr className="mt-3 mb-1 hidden-lg-down" />
        <div
          className="mx-auto"
          style={{
            maxWidth: '729px',
          }}
        >
          <hr className="mt-2 mt-md-0 mb-2" />
          <AppraiserSellModule className="" isAppraiserTabDefault makeModelEnabled canBeClosed={false} isEmbedded />
          <hr className="my-1_5" />
        </div>
        <div className="text-center text-md-start">
          <Share url={canonicalURL} title={content.title} />
        </div>
        <AllAuthorsMetadata allAuthors={allAuthors} />
        <ErrorBoundary>
          <VehicleShowcase content={linkWidgetContent.child('home-vehicle-showcase-v2')} borderHiddenMdDown />
        </ErrorBoundary>
      </Container>
    </main>
  );
}

ObdDtcCodeUI.propTypes = {
  content: CmsEntities.Content,
  allAuthors: PropTypes.shape({
    author: CmsEntities.Content,
    coAuthor: CmsEntities.Content,
    editor: CmsEntities.Content,
  }),
  linkWidgetContent: CmsEntities.Content,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const ObdDtcCode = connect(mapStateToProps)(
  connectToModel(ObdDtcCodeUI, {
    content: bindToContent(({ codeId }) => getResearchArticleContentPath(ARTICLE_CATEGORY, codeId)),
    allAuthors: bindToPath(
      ({ codeId }) =>
        CmsPaths.buildAllAuthorsPath({
          contentPath: getResearchArticleContentPath(ARTICLE_CATEGORY, codeId),
        }),
      CmsModel,
      transformAllAuthorsContent
    ),
    linkWidgetContent: bindToContent(RESEARCH_LINK_WIDGET_EDITORIAL_PATH),
  })
);
